import { CheckCircleOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Radio } from "antd";
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  createProtocolTemplate,
  type CreateProtocolTemplatePayload,
  type ProtocolTemplateType,
} from "../../../protocols/protocolsService";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { useAuth } from "../../../../lib/auth/AuthContext";
import { getAuditFields } from "../../../../shared/utils/auditFields";

interface SaveTemplateModalProps {
  open: boolean;
  protocolId: string | null;
  protocolName: string;
  orgId: string;
  workspaceId: string;
  onClose: () => void;
}

const SaveTemplateModal = ({
  open,
  protocolId,
  protocolName,
  orgId,
  workspaceId,
  onClose,
}: SaveTemplateModalProps): ReactElement => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [templateType, setTemplateType] =
    useState<ProtocolTemplateType>("universal");
  const [templateName, setTemplateName] = useState<string>("");
  const [isSavingTemplate, setIsSavingTemplate] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setTemplateType("universal");
      setTemplateName(
        `${protocolName} ${formatTranslation(
          t("createProtocol.templateModal.defaultTemplateName")
        )}`
      );
    }
  }, [open, protocolName, t]);

  const handleSaveTemplate = async (): Promise<void> => {
    if (!protocolId) {
      toast.error(
        formatTranslation(
          t("createProtocol.templateModal.toast.protocolRequired")
        )
      );
      return;
    }

    const trimmedTemplateName = templateName.trim();
    if (!trimmedTemplateName) {
      return;
    }

    if (templateType === "workspace" && !workspaceId) {
      toast.error(
        formatTranslation(
          t("createProtocol.templateModal.toast.workspaceRequired")
        )
      );
      return;
    }

    const basePayload: CreateProtocolTemplatePayload = {
      protocol_id: protocolId,
      template_name: trimmedTemplateName,
      template_type: templateType,
      ...getAuditFields("post", user?.id),
    };

    const payload: CreateProtocolTemplatePayload =
      templateType === "workspace"
        ? {
            ...basePayload,
            workspace_id: workspaceId,
          }
        : basePayload;

    setIsSavingTemplate(true);
    try {
      await createProtocolTemplate(payload, orgId, workspaceId);
      toast.success(
        formatTranslation(t("createProtocol.templateModal.toast.saveSuccess"))
      );
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        formatTranslation(t("createProtocol.templateModal.toast.saveError"));
      toast.error(errorMessage);
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return (
    <Modal
      open={open}
      className="save-template-modal"
      title={
        <div className="save-template-modal-title">
          <CheckCircleOutlined style={{ color: "#14803C", fontSize: 18 }} />
          <span>
            {formatTranslation(t("createProtocol.templateModal.title"))}
          </span>
        </div>
      }
      onCancel={onClose}
      footer={
        <div className="save-template-modal-footer">
          <Button size="middle" onClick={onClose}>
            {formatTranslation(t("modal.cancel"))}
          </Button>
          <Button
            className="save-template-btn"
            type="primary"
            size="middle"
            onClick={() => {
              void handleSaveTemplate();
            }}
            disabled={!templateName.trim() || isSavingTemplate}
            loading={isSavingTemplate}
          >
            {formatTranslation(t("createProtocol.templateModal.saveButton"))}
          </Button>
        </div>
      }
    >
      <div className="save-template-modal-content">
        <Radio.Group
          className="save-template-radio-group"
          onChange={(event) => {
            setTemplateType(event.target.value as ProtocolTemplateType);
          }}
          value={templateType}
        >
          <Radio value="universal">
            {formatTranslation(
              t("createProtocol.templateModal.universalOption")
            )}
          </Radio>
          <Radio value="workspace">
            {formatTranslation(
              t("createProtocol.templateModal.workspaceOption")
            )}
          </Radio>
        </Radio.Group>
        <div className="template-name-field">
          <label htmlFor="template-name-input">
            {formatTranslation(t("createProtocol.basicInfo.templateName"))}
          </label>
          <Input
            id="template-name-input"
            value={templateName}
            size="middle"
            onChange={(event) => {
              setTemplateName(event.target.value);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SaveTemplateModal;
